import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axiosInstance from '../utils/axiosConfig';
import DocumentNameSuggestionForm from './DocumentNaming/DocumentNameSuggestionForm';
// Bunkers Phase 1 (May 2026): reuse the delivery upload modal's
// overlay/header/footer styles so the new AI naming confirm step in
// DealDocumentsPanel matches the delivery upload UX visually.
import './DeliveryDocumentUploadModal.css';
import { confirmName, lookupNames } from '../services/documentNamingService';
import SuggestedName from './DocumentNaming/SuggestedName';
import useSuggestionPolling from './DocumentNaming/useSuggestionPolling';
import {
  DEAL_DOCUMENT_CATEGORIES,
  filterDealCategoriesForCommodity,
} from '../constants/documentTypeCategories';
import './DealDocumentsPanel.css';

/**
 * DealDocumentsPanel
 *
 * Inline panel that lists, uploads, downloads and deletes deal documents that
 * live in the DocuWare deal cabinet (DOCUWARE_DEAL_CABINET_ID). Mirrors the
 * delivery document panel two-step upload flow (create-record then
 * upload-file) but renders inline (not as a slide-out drawer) so it can sit
 * on the Bid/Offer and Trade Recap screens directly.
 *
 * Documents are correlated by TRANSACTION_NUMBER, which is unique across
 * Bid/Offers, Trade Recaps and Freight Recaps - this means a recap can carry
 * documents even before any deal/offer is created.
 *
 * Props:
 *   offerId          - HMSUserOffer.offer_id (one of these three is required)
 *   tradeRecapId     - TradeRecap.id
 *   freightRecapId   - FreightRecap.id
 *   title            - heading text (defaults to "Deal Documents")
 *   employeeId       - optional uploader id captured on each new document
 */
// Bunkers Phase 1 (May 2026): the per-upload document type picker was
// removed from the panel UI and every manual upload was tagged
// ``Other``. That constant was handed to the naming service as the
// document's type, so the extraction agent was told "Other" on every
// upload and every deal document came out named ``DOC``.
//
// REINSTATED September 2026: the picker is back, and wider. It now
// offers the delivery vocabulary as well as the deal-native types --
// see ``DEAL_DOCUMENT_CATEGORIES`` in
// frontend/src/constants/documentTypeCategories.js, which is the single
// source shared with the delivery upload modal.

const FILE_ICON_CLASS_BY_EXT = {
  pdf: 'deal-documents-panel__file-icon--pdf',
  doc: 'deal-documents-panel__file-icon--doc',
  docx: 'deal-documents-panel__file-icon--doc',
  xls: 'deal-documents-panel__file-icon--xls',
  xlsx: 'deal-documents-panel__file-icon--xls',
  csv: 'deal-documents-panel__file-icon--xls',
  png: 'deal-documents-panel__file-icon--img',
  jpg: 'deal-documents-panel__file-icon--img',
  jpeg: 'deal-documents-panel__file-icon--img',
  gif: 'deal-documents-panel__file-icon--img',
  eml: 'deal-documents-panel__file-icon--mail',
  msg: 'deal-documents-panel__file-icon--mail',
  txt: 'deal-documents-panel__file-icon--txt',
};

const getFileExt = (filename) => {
  if (!filename) return '';
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx + 1).toLowerCase() : '';
};

// DocuWare ships dates as Microsoft JSON ``/Date(1716750000000)/``
// (sometimes with a timezone offset suffix). ``new Date(...)`` can't
// parse that format and silently produces an Invalid Date whose
// ``toLocaleString`` literally renders as ``"Invalid Date"``. Normalise
// known shapes here and return ``null`` on anything we can't make sense
// of so callers can skip rendering entirely.
const parseDocuWareDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const msMatch = value.match(/\/Date\((-?\d+)(?:[+-]\d{4})?\)\//);
    if (msMatch) {
      const d = new Date(parseInt(msMatch[1], 10));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const num = Number(bytes);
  if (Number.isNaN(num) || num <= 0) return '';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / 1024 / 1024).toFixed(1)} MB`;
};

const UploadIcon = () => (
  <svg
    className="deal-documents-panel__upload-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const FolderIcon = () => (
  <svg className="deal-documents-panel__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-7l-2-2H5a2 2 0 0 0-2 2z" />
  </svg>
);

const DealDocumentsPanel = ({
  offerId,
  tradeRecapId,
  freightRecapId,
  title = 'Deal Documents',
  employeeId,
  partnerName,
  commodityType,
}) => {
  const [documents, setDocuments] = useState([]);
  const [displayNamesByDocId, setDisplayNamesByDocId] = useState({});
  useSuggestionPolling(displayNamesByDocId, setDisplayNamesByDocId, 'deal_document');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  // The user's answer to "what kind of document is this?". Empty until
  // they choose: the upload is blocked and the AI naming step does not
  // run until it is set, so nothing can be filed as a guess.
  const [selectedType, setSelectedType] = useState('');
  const fileInputRef = useRef(null);

  // Bunkers category only where the record is known NOT to be fuels do
  // we hide it -- freight recaps carry no commodity flag and still file
  // bunker delivery notes. Delivery upload uses the stricter rule.
  const typeCategories = useMemo(
    () => filterDealCategoriesForCommodity(DEAL_DOCUMENT_CATEGORIES, commodityType),
    [commodityType]
  );

  // Bunkers Phase 1 (May 2026): pre-upload AI naming step.
  // ``pendingFile`` is set when the user picks a file; the upload
  // doesn't fire until they review the AI suggestion in the modal
  // and click Confirm. ``pendingNamingPayload`` mirrors the shape
  // emitted by ``DocumentNameSuggestionForm.onChange``.
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingNamingPayload, setPendingNamingPayload] = useState(null);

  const contextParams = useMemo(() => {
    if (offerId) return { offer_id: offerId };
    if (tradeRecapId) return { trade_recap_id: tradeRecapId };
    if (freightRecapId) return { freight_recap_id: freightRecapId };
    return null;
  }, [offerId, tradeRecapId, freightRecapId]);

  const isScoped = !!contextParams;

  const getField = (doc, name) => {
    if (!doc || !Array.isArray(doc.Fields)) return '';
    const field = doc.Fields.find((f) => f && f.FieldName === name);
    if (!field) return '';
    const value = field.Item;
    if (value && typeof value === 'object' && 'Value' in value) return value.Value;
    return value === undefined || value === null ? '' : value;
  };

  const fetchDocuments = useCallback(async () => {
    if (!isScoped) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/api/docuware/deal-documents/documents/', {
        params: { ...contextParams, count: 100 },
      });
      const docs = Array.isArray(res.data?.Items) ? res.data.Items : [];
      setDocuments(docs);
      
      try {
        const docIds = docs.map(d => String(d?.Id || d?.id)).filter(Boolean);
        if (docIds.length > 0) {
          const map = await lookupNames(docIds, 'deal_document');
          setDisplayNamesByDocId(map || {});
        }
      } catch (lookupErr) {
        console.warn('Failed to lookup AI display names for deal docs (non-fatal):', lookupErr);
      }
    } catch (err) {
      console.error('Failed to load deal documents:', err);
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [isScoped, contextParams]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Bunkers Phase 1 (May 2026): file-picker callback. Stashes the
  // file and opens the AI naming review modal. Upload is deferred to
  // ``handleConfirmUpload`` so the user can edit / accept the AI
  // suggestion before bytes are sent.
  const handleFileSelected = (event) => {
    const file = event.target.files && event.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file || !isScoped) return;

    const maxSizeBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError('File size exceeds the 10MB limit.');
      return;
    }

    setError(null);
    setPendingNamingPayload(null);
    setSelectedType('');
    setPendingFile(file);
  };

  const handleCancelUpload = () => {
    if (isUploading) return;
    setPendingFile(null);
    setPendingNamingPayload(null);
    setSelectedType('');
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile || !selectedType || !isScoped || isUploading) return;
    const file = pendingFile;

    setIsUploading(true);
    setError(null);
    try {
      const recordPayload = {
        ...contextParams,
        employee_id: employeeId || undefined,
        subject: file.name,
        deal_document_type: selectedType,
        document_source: 'manual',
      };
      const recordRes = await axiosInstance.post(
        '/api/docuware/deal-documents/create-record/',
        recordPayload,
      );
      const documentId = recordRes.data?.document_id;
      if (!documentId) {
        throw new Error('Failed to create document record.');
      }

      const fileForm = new FormData();
      fileForm.append('file', file);
      await axiosInstance.post(
        `/api/docuware/deal-documents/upload-file/${documentId}/`,
        fileForm,
      );

      try {
        await axiosInstance.post('/api/offer/deal-documents/', {
          file_name: file.name,
          file_size: file.size,
          content_type: file.type || '',
          source: 'manual',
          deal_document_type: selectedType,
          docuware_document_id: String(documentId),
          ...(offerId ? { offer: offerId } : {}),
          ...(tradeRecapId ? { trade_recap: tradeRecapId } : {}),
          ...(freightRecapId ? { freight_recap: freightRecapId } : {}),
          ...(employeeId ? { uploaded_by_employee_id: employeeId } : {}),
        });
      } catch (auditErr) {
        // Local audit row is best-effort; DocuWare is the source of truth.
        console.warn('Local DealDocument audit row failed:', auditErr);
      }

      try {
        const relatedEntityId = offerId || tradeRecapId || freightRecapId || '';
        // Prefer the payload emitted by ``DocumentNameSuggestionForm``
        // (carries user edits + AI snapshot + per-field override
        // tracking). Fall back to the legacy auto-generated components
        // if the user somehow confirmed before the suggestion ran.
        if (pendingNamingPayload && pendingNamingPayload.components) {
          await confirmName({
            docuwareDocumentId: String(documentId),
            module: 'deal_document',
            file,
            typeDropdownValue: pendingNamingPayload.typeDropdownValue || selectedType || '',
            partnerName: pendingNamingPayload.partnerName || partnerName || '',
            relatedEntityId: relatedEntityId ? String(relatedEntityId) : '',
            components: pendingNamingPayload.components,
            aiSuggestion: pendingNamingPayload.aiSuggestion
              || { type_dropdown_value: selectedType },
            userOverrides: pendingNamingPayload.userOverrides || [],
            originalFilename: file.name,
          });
        } else {
          const today = new Date().toISOString().slice(0, 10);
          await confirmName({
            docuwareDocumentId: String(documentId),
            module: 'deal_document',
            file,
            typeDropdownValue: selectedType || '',
            partnerName: partnerName || '',
            relatedEntityId: relatedEntityId ? String(relatedEntityId) : '',
            components: {
              component_date: today,
              component_type_code: 'DOC',
              component_doc_number: '',
              component_partner_name: partnerName || '',
              component_location_flag: '',
            },
            aiSuggestion: { type_dropdown_value: selectedType },
            userOverrides: [],
            originalFilename: file.name,
          });
        }
      } catch (nameErr) {
        console.warn('Failed to persist AI display name (non-fatal):', nameErr);
      }

      await fetchDocuments();
      setPendingFile(null);
      setPendingNamingPayload(null);
      setSelectedType('');
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.error || err.message || 'Failed to upload document.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const res = await axiosInstance.get(
        `/api/docuware/deal-documents/download/${docId}/`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `deal-document-${docId}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download document.');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document from DocuWare? This cannot be undone.')) return;
    try {
      await axiosInstance.delete(`/api/docuware/deal-documents/delete/${docId}/`);
      await fetchDocuments();
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete document.');
    }
  };

  if (!isScoped) {
    return null;
  }

  const renderRow = (doc) => {
    const id = doc.Id || doc.id;
    const filename = getField(doc, 'SUBJECT') || doc.Title || `Document ${id}`;
    const nameRow = displayNamesByDocId?.[String(id)] || null;
    const aiDisplayName = nameRow?.display_name || null;
    const onNameRow = (updated) => setDisplayNamesByDocId((prev) => ({ ...(prev || {}), [String(id)]: updated }));
    const renderName = aiDisplayName || filename;
    const sourceField = getField(doc, 'DOCUMENT_SOURCE');
    const dateUploaded = parseDocuWareDate(
      getField(doc, 'DATE_UPLOADED') || doc.CreatedAt,
    );
    const fileSize = doc.FileSize || doc.Size;
    const ext = getFileExt(filename);
    const iconClass =
      FILE_ICON_CLASS_BY_EXT[ext] || 'deal-documents-panel__file-icon--default';
    const isHamster = sourceField === 'hamster_builder';

    return (
      <li key={id} className="deal-documents-panel__item">
        <div className={`deal-documents-panel__file-icon ${iconClass}`}>
          {ext ? ext.slice(0, 4) : 'FILE'}
        </div>
        <div className="deal-documents-panel__file-info">
          <button
            type="button"
            className="deal-documents-panel__file-name"
            onClick={() => handleDownload(id, filename)}
            title={filename}
          >
            {renderName}
          </button>
          <SuggestedName row={nameRow} onChanged={onNameRow} />
          <div className="deal-documents-panel__file-meta">
            {formatFileSize(fileSize)}
            {fileSize ? ' • ' : ''}
            {dateUploaded ? dateUploaded.toLocaleString() : ''}
          </div>
        </div>
        {/* Bunkers Phase 1 (May 2026): per-doc type cell removed --
            the picker is gone and the value was always blank for
            manual uploads. The duplicate standalone date cell was
            also removed since the upload time already shows under
            the filename in ``__file-meta`` above. */}
        <div className="deal-documents-panel__cell">
          <span
            className={`deal-documents-panel__source-badge ${
              isHamster
                ? 'deal-documents-panel__source-badge--hamster'
                : 'deal-documents-panel__source-badge--manual'
            }`}
          >
            {isHamster ? 'Hamster Builder' : 'Manual'}
          </span>
        </div>
        <div className="deal-documents-panel__actions">
          <button
            type="button"
            className="deal-documents-panel__icon-btn"
            onClick={() => handleDownload(id, filename)}
            aria-label="Download"
            title="Download"
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className="deal-documents-panel__icon-btn deal-documents-panel__icon-btn--danger"
            onClick={() => handleDelete(id)}
            aria-label="Delete"
            title="Delete"
          >
            <TrashIcon />
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="deal-documents-panel">
      <div className="deal-documents-panel__header">
        <div className="deal-documents-panel__title-block">
          <h3 className="deal-documents-panel__title">{title}</h3>
          {documents.length > 0 && (
            <span className="deal-documents-panel__count">{documents.length}</span>
          )}
        </div>
        <div className="deal-documents-panel__controls">
          {/* Bunkers Phase 1 (May 2026): document type picker removed
              from the UI per product. ``selectedType`` stays in state
              with its default of 'Other' so the upload flow continues
              to satisfy the backend's required ``deal_document_type``
              field without a behaviour change. */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelected}
            style={{ display: 'none' }}
            accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.eml,.msg,.xlsx,.xls,.csv"
          />
          <button
            type="button"
            className="deal-documents-panel__upload-btn"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={isUploading}
          >
            <UploadIcon />
            {isUploading ? 'Uploading…' : 'Upload Document'}
          </button>
        </div>
      </div>

      {error && <div className="deal-documents-panel__error">{error}</div>}

      <div className="deal-documents-panel__body">
        {loading ? (
          <div className="deal-documents-panel__loading">Loading documents…</div>
        ) : documents.length === 0 ? (
          <div className="deal-documents-panel__empty">
            <FolderIcon />
            <p className="deal-documents-panel__empty-title">No documents uploaded yet</p>
            <p className="deal-documents-panel__empty-sub">
              Upload contracts, recap emails, certificates or other deal-related files.
              Hamster Builder source files appear here automatically.
            </p>
          </div>
        ) : (
          <ul className="deal-documents-panel__list">
            {documents.map(renderRow)}
          </ul>
        )}
      </div>

      {/* Bunkers Phase 1 (May 2026): pre-upload AI naming review modal.
          Mirrors the inline ``DocumentNameSuggestionForm`` step from
          DeliveryDocumentUploadModal. Reuses the delivery upload
          modal's overlay/header/footer classes so it looks identical
          across the two surfaces. ``autoRun`` makes the AI suggestion
          fire immediately when the modal mounts -- no extra clicks. */}
      {pendingFile && (
        <div
          className="delivery-doc-upload-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancelUpload();
          }}
        >
          <div className="delivery-doc-upload-modal">
            <div className="delivery-doc-upload-modal-header">
              <h3 className="delivery-doc-upload-modal-title">
                Name your document
              </h3>
              <button
                type="button"
                className="delivery-doc-upload-close-button"
                onClick={handleCancelUpload}
                disabled={isUploading}
                aria-label="Close modal"
              >
                {'\u00D7'}
              </button>
            </div>

            <div className="delivery-doc-upload-modal-body">
              <div className="delivery-doc-upload-form-group">
                <div
                  className="delivery-doc-selected-category"
                  style={{
                    borderLeftColor: '#4a6b25',
                    backgroundColor: '#4a6b2510',
                  }}
                >
                  <span
                    className="delivery-doc-selected-category-badge"
                    style={{ backgroundColor: '#4a6b25' }}
                  >
                    {pendingFile.name}
                  </span>
                </div>
              </div>

              {/* Document type picker. Same vocabulary as the delivery
                  upload modal, grouped by the same categories, but in a
                  single control: on a recap the file has already been
                  chosen, so the delivery modal's separate category step
                  would be a click for nothing. Required -- the naming
                  step below stays hidden until it is answered, because
                  the chosen type is what the extraction agent reads the
                  document as. */}
              <div className="delivery-doc-upload-form-group">
                <label
                  className="delivery-doc-upload-label"
                  htmlFor="deal-doc-type-select"
                >
                  Select Document Type:{' '}
                  <span className="delivery-doc-upload-label-required">*</span>
                </label>
                <select
                  id="deal-doc-type-select"
                  className="delivery-doc-upload-select"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  disabled={isUploading}
                >
                  <option value="">-- Select Document Type --</option>
                  {typeCategories.map((cat) => (
                    <optgroup key={cat.id} label={cat.label}>
                      {cat.types.map((docType) => (
                        <option key={docType} value={docType}>
                          {docType}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {selectedType ? (
                <DocumentNameSuggestionForm
                  file={pendingFile}
                  module="deal_document"
                  relatedEntityId={
                    offerId || tradeRecapId || freightRecapId || ''
                  }
                  typeDropdownValue={selectedType}
                  partnerName={partnerName || ''}
                  onChange={setPendingNamingPayload}
                  disabled={isUploading}
                  autoRun
                />
              ) : (
                <div className="delivery-doc-upload-file-hint">
                  Choose a document type and the name will be suggested for you.
                </div>
              )}
            </div>

            <div className="delivery-doc-upload-modal-footer">
              <button
                type="button"
                className="delivery-doc-upload-btn delivery-doc-upload-btn-secondary"
                onClick={handleCancelUpload}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delivery-doc-upload-btn delivery-doc-upload-btn-primary"
                onClick={handleConfirmUpload}
                /* Blocked until a type is chosen, and while the AI
                   suggestion is in flight so a click through the spinner
                   can't file a half-named document. An AI failure still
                   leaves the upload available -- the user can type the
                   name themselves or upload as-is. */
                disabled={
                  isUploading
                  || !selectedType
                  || !!pendingNamingPayload?.isLoading
                }
              >
                {isUploading ? 'Uploading…' : 'Upload Document'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealDocumentsPanel;
